import { useEffect, useState } from 'react';
import type { ComponentType } from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar,
  Facebook,
  Images,
  Instagram,
  Link2,
  Linkedin,
  Mail,
  MapPin,
  Music2,
  Phone,
  Play,
  Twitter,
  Youtube,
} from 'lucide-react';
import { useAuth } from '@proptr/api-client/react';
import type { BillingSummary } from '@proptr/api-client';
import type {
  ChannelCount,
  DashboardStats,
  StatBucket,
  TopAgent,
  TopButton,
} from '@proptr/shared';
import { AppShell } from '../components/layout/AppShell.js';
import { WhatsAppIcon } from '../components/WhatsAppIcon.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { isTestMode } from '../lib/testMode.js';
import { initials } from '../lib/utils.js';
import { client } from '../main.js';

export function DashboardPage() {
  const { user } = useAuth();
  const today = new Date().toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [channelRange, setChannelRange] = useState<'thisMonth' | 'sinceStart'>(
    'thisMonth',
  );
  useEffect(() => {
    let alive = true;
    // Plan + affiliate usage for the subscription card. `lite` skips the
    // Stripe round-trips the card doesn't need.
    client.billing
      .summary({ lite: true })
      .then((b) => {
        if (alive) setBilling(b);
      })
      .catch(() => {
        // Non-critical — the card just stays hidden.
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    client.dashboard
      .stats()
      .then((s) => {
        if (alive) setStats(s);
      })
      .catch((err: Error) => {
        if (alive) setStatsError(err.message);
      });
    return () => {
      alive = false;
    };
  }, []);

  return (
    <AppShell>
      <div className="space-y-8 pb-10">
        <div className="flex flex-wrap items-start justify-between gap-6 border-b border-[hsl(var(--hairline))] pb-8">
          <div className="min-w-0">
            <div className="eyebrow mb-3">Overview / {today}</div>
            <h1 className="font-display text-[56px] font-medium leading-none tracking-[-0.025em]">
              Good to see you 👋
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              Signed in as <span className="text-foreground">{user?.email}</span> · role{' '}
              <span className="text-foreground">{user?.role}</span>
            </p>
          </div>
          {/* Subscription/billing is left out of the test-mode demo. */}
          {!isTestMode() && <SubscriptionCard billing={billing} />}
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <StatFromBucket
            label="Views"
            bucket={stats?.views}
            hint="proptree page loads · month"
            loading={!stats && !statsError}
          />
          <StatFromBucket
            label="Clicks"
            bucket={stats?.clicks}
            hint="outbound clicks · month"
            loading={!stats && !statsError}
          />
          <StatFromBucket
            label="Inquiries"
            bucket={stats?.inquiries}
            hint="contact requests · month"
            loading={!stats && !statsError}
          />
        </div>
        {statsError && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 font-mono text-[11px] text-destructive">
            Couldn't load dashboard stats: {statsError}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <Card className="p-6 xl:col-span-2">
            <div className="mb-6 flex items-start justify-between gap-3">
              <div>
                <div className="eyebrow">Traffic sources</div>
                <h2 className="mt-1 font-sans text-[18px] font-semibold tracking-[-0.01em]">
                  Where your views come from
                </h2>
              </div>
              <RangeTabs value={channelRange} onChange={setChannelRange} />
            </div>
            <TrafficSources stats={stats} range={channelRange} />
          </Card>

          <Card className="relative flex flex-col overflow-hidden p-6">
            <div className="mb-6 flex items-start justify-between gap-3">
              <h2 className="font-sans text-[18px] font-semibold tracking-[-0.01em]">
                Share of views
              </h2>
              <RangeTabs value={channelRange} onChange={setChannelRange} />
            </div>
            <ShareOfViews stats={stats} range={channelRange} />
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
          <RankingCard
            eyebrow="Top buttons · this month"
            title="Most clicked actions"
            rows={topButtonsToRows(stats?.topButtons)}
            type="button"
            loading={!stats && !statsError}
          />
          <RankingCard
            eyebrow="Top proptrees · this month"
            title="Best performing agents"
            rows={topAgentsToRows(stats?.topAgents)}
            type="proptree"
            loading={!stats && !statsError}
          />
        </div>
      </div>
    </AppShell>
  );
}

// Subscription snapshot beside the greeting — kept compact so it never
// stands taller than the greeting block it sits next to.
function SubscriptionCard({ billing }: { billing: BillingSummary | null }) {
  if (!billing) return null;
  const planLabel =
    billing.plan === 'trial'
      ? 'Trial'
      : `Proptree ${billing.plan.charAt(0).toUpperCase()}${billing.plan.slice(1)}`;
  const total = billing.affiliatesIncluded + billing.affiliateAddon;
  const used = billing.affiliatesUsed;
  const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;
  const full = total > 0 && used >= total;
  return (
    <Card className="w-[300px] shrink-0 p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="mono-label text-muted-foreground">Subscription</div>
          <div className="mt-0.5 flex items-center gap-2">
            <span className="truncate font-display text-[16px] font-medium leading-tight text-foreground">
              {planLabel}
            </span>
            {billing.plan !== 'trial' && (
              <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-success" />
            )}
          </div>
        </div>
        <Button asChild size="sm" variant="outline" className="shrink-0">
          <Link to="/billing">
            {billing.plan === 'trial' ? 'Choose plan' : 'Billing'}
          </Link>
        </Button>
      </div>

      <div className="mt-3.5">
        <div className="flex items-baseline justify-between">
          <span className="mono-label text-muted-foreground">
            {full ? 'Affiliates · full' : 'Affiliates'}
          </span>
          <span className="font-mono text-[11px] font-semibold tabular-nums">
            {used} / {total}
          </span>
        </div>
        <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full transition-[width] duration-500 ease-out ${
              full ? 'bg-warning' : 'bg-primary'
            }`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </Card>
  );
}

interface Source {
  name: string;
  count: number;
  pct: number;
  color: string;
}

// Display label + brand-ish color per raw channel key.
const CHANNEL_META: Record<string, { label: string; color: string }> = {
  instagram: { label: 'Instagram', color: 'hsl(var(--primary))' },
  facebook: { label: 'Facebook', color: 'hsl(var(--primary) / 0.55)' },
  meta: { label: 'Meta', color: 'hsl(var(--primary) / 0.4)' },
  whatsapp: { label: 'WhatsApp', color: 'hsl(var(--success))' },
  tiktok: { label: 'TikTok', color: 'hsl(var(--foreground) / 0.9)' },
  youtube: { label: 'YouTube', color: 'hsl(var(--destructive))' },
  snapchat: { label: 'Snapchat', color: 'hsl(48 96% 58%)' },
  linkedin: { label: 'LinkedIn', color: 'hsl(210 80% 48%)' },
  x: { label: 'X', color: 'hsl(var(--foreground))' },
  google: { label: 'Google', color: 'hsl(220 80% 55%)' },
  bing: { label: 'Bing', color: 'hsl(170 60% 45%)' },
  pinterest: { label: 'Pinterest', color: 'hsl(354 75% 55%)' },
  reddit: { label: 'Reddit', color: 'hsl(16 100% 55%)' },
  telegram: { label: 'Telegram', color: 'hsl(200 85% 55%)' },
  wechat: { label: 'WeChat', color: 'hsl(140 60% 45%)' },
  email: { label: 'Email', color: 'hsl(var(--muted-foreground))' },
  llm: { label: 'AI assistants', color: 'hsl(280 55% 55%)' },
  direct: { label: 'Direct', color: 'hsl(var(--foreground) / 0.75)' },
  referral: { label: 'Referral', color: 'hsl(var(--muted-foreground) / 0.9)' },
  medium: { label: 'Medium', color: 'hsl(var(--foreground) / 0.6)' },
  tumblr: { label: 'Tumblr', color: 'hsl(210 60% 30%)' },
};
const OTHER_COLOR = 'hsl(var(--muted-foreground) / 0.55)';

// Turn the live channel payload into display-shaped Source[]. The total
// comes from the authoritative page-load counter (views.thisMonth /
// views.sinceStart) — NOT the sum of known channels, because many page
// loads may not be attributable to a channel. The difference is lumped into
// "Other", matching the legacy dashboard (e.g. 44 total − 6 attributed = 38
// Other for this test enterprise since start).
function buildSources(
  channels: ChannelCount[],
  field: 'thisMonth' | 'sinceStart',
  total: number,
): { sources: Source[]; total: number } {
  const ranked = channels
    .map((c) => ({ key: c.name, count: c[field] }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count);

  const known: Array<{ key: string; count: number }> = [];
  let unknownTail = 0;
  for (const c of ranked) {
    if (CHANNEL_META[c.key]) known.push(c);
    else unknownTail += c.count;
  }

  const pct = (n: number) => (total > 0 ? Math.round((n / total) * 100) : 0);
  const attributed =
    known.reduce((s, c) => s + c.count, 0) + unknownTail;
  const unattributed = Math.max(0, total - attributed);
  const otherCount = unknownTail + unattributed;

  const sources: Source[] = known.map((c) => {
    const meta = CHANNEL_META[c.key]!;
    return { name: meta.label, count: c.count, pct: pct(c.count), color: meta.color };
  });
  if (otherCount > 0) {
    sources.push({
      name: 'Other',
      count: otherCount,
      pct: pct(otherCount),
      color: OTHER_COLOR,
    });
  }

  return { sources, total };
}

type RangeKey = 'thisMonth' | 'sinceStart';

function RangeTabs({
  value,
  onChange,
}: {
  value: RangeKey;
  onChange: (v: RangeKey) => void;
}) {
  const btn = (key: RangeKey, label: string) => (
    <button
      key={key}
      type="button"
      onClick={() => onChange(key)}
      aria-pressed={value === key}
      className={
        value === key
          ? 'rounded-full bg-card px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-foreground shadow-sm'
          : 'rounded-full px-2.5 py-1 font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground transition-colors hover:text-foreground'
      }
    >
      {label}
    </button>
  );
  return (
    <div className="inline-flex shrink-0 items-center rounded-full border border-[hsl(var(--hairline))] bg-muted p-0.5">
      {btn('thisMonth', 'This month')}
      {btn('sinceStart', 'Since start')}
    </div>
  );
}

function TrafficSources({
  stats,
  range,
}: {
  stats: DashboardStats | null;
  range: RangeKey;
}) {
  if (!stats) {
    return (
      <div className="space-y-5">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-2">
            <div className="h-3 w-full animate-pulse rounded bg-muted" />
            <div className="h-1.5 w-full animate-pulse rounded-full bg-muted" />
          </div>
        ))}
      </div>
    );
  }
  const { sources, total } = buildSources(
    stats.channels,
    range,
    stats.views[range],
  );
  if (sources.length === 0) {
    return (
      <div className="py-8 text-center font-mono text-[11px] text-muted-foreground">
        {range === 'thisMonth'
          ? 'No traffic data for this month yet.'
          : 'No traffic data yet.'}
      </div>
    );
  }
  return (
    <>
      <div className="space-y-5">
        {sources.map((s) => (
          <SourceRow key={s.name} {...s} total={total} />
        ))}
      </div>
      <div className="mt-6 flex items-center justify-between border-t border-[hsl(var(--hairline))] pt-4">
        <span className="mono-label text-muted-foreground">Total</span>
        <span className="font-display text-[20px] font-medium tracking-[-0.02em] numeral">
          {total.toLocaleString()}
        </span>
      </div>
    </>
  );
}

function ShareOfViews({
  stats,
  range,
}: {
  stats: DashboardStats | null;
  range: RangeKey;
}) {
  if (!stats) {
    return (
      <div className="flex flex-col items-center gap-4 py-8">
        <div className="h-40 w-40 animate-pulse rounded-full bg-muted" />
        <div className="h-3 w-32 animate-pulse rounded bg-muted" />
      </div>
    );
  }
  const { sources } = buildSources(stats.channels, range, stats.views[range]);
  if (sources.length === 0) {
    return (
      <div className="py-12 text-center font-mono text-[11px] text-muted-foreground">
        {range === 'thisMonth'
          ? 'No traffic data for this month yet.'
          : 'No traffic data yet.'}
      </div>
    );
  }
  // Delta only makes sense for this month; hide it when showing all-time.
  const deltaPct = range === 'thisMonth' ? stats.views.deltaPct : undefined;
  return (
    <>
      <Donut sources={sources} deltaPct={deltaPct} />
      <div className="mt-7 divide-y divide-[hsl(var(--hairline))]">
        {sources.map((s) => (
          <div
            key={s.name}
            className="flex items-center gap-3 py-2.5 first:pt-0 last:pb-0"
          >
            <span
              className="h-2.5 w-2.5 shrink-0 rounded-full"
              style={{
                background: s.color,
                boxShadow: `0 0 0 3px hsl(var(--card)), 0 0 0 4px ${s.color.includes('/') ? s.color : `${s.color} / 0.2`}`,
              }}
            />
            <span className="flex-1 truncate font-sans text-[13px] font-medium">
              {s.name}
            </span>
            <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
              {formatShortCount(s.count)}
            </span>
            <span className="w-10 text-right font-display text-[15px] font-medium tracking-[-0.01em] numeral tabular-nums">
              {s.pct}%
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

function formatShortCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

function SourceRow({
  name,
  count,
  pct,
  color,
  total,
}: Source & { total: number }) {
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: color }} />
          <span className="font-sans text-[13px] font-medium">{name}</span>
        </div>
        <div className="flex items-baseline gap-3">
          <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
            {count.toLocaleString()} / {total.toLocaleString()}
          </span>
          <span className="font-display text-[16px] font-medium tracking-[-0.02em] numeral tabular-nums">
            {pct}%
          </span>
        </div>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full transition-[width] duration-500 ease-out"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
}

function Donut({
  sources,
  deltaPct,
}: {
  sources: Source[];
  deltaPct?: number | null;
}) {
  // SVG stroke-dasharray trick: each segment is an arc of the same circle.
  const size = 220;
  const stroke = 22;
  const r = (size - stroke) / 2;
  const cx = size / 2;
  const cy = size / 2;
  const circumference = 2 * Math.PI * r;
  // Small gap (in px along the circumference) between adjacent segments
  const gapLen = 3;

  const total = sources.reduce((s, x) => s + x.count, 0);
  const totalLabel =
    total >= 10000 ? `${(total / 1000).toFixed(1)}k` : total.toLocaleString();

  let offset = 0;
  const segments = sources.map((s) => {
    const fullLen = (s.pct / 100) * circumference;
    const drawLen = Math.max(0, fullLen - gapLen);
    const seg = {
      ...s,
      dash: `${drawLen} ${circumference - drawLen}`,
      dashOffset: -offset,
    };
    offset += fullLen;
    return seg;
  });

  return (
    <div className="relative flex items-center justify-center py-2">
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        className="relative -rotate-90"
      >
        {/* Track */}
        <circle
          cx={cx}
          cy={cy}
          r={r}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={stroke}
        />
        {/* Inner subtle border on inside of donut */}
        <circle
          cx={cx}
          cy={cy}
          r={r - stroke / 2}
          fill="none"
          stroke="hsl(var(--hairline))"
          strokeWidth={1}
        />
        <circle
          cx={cx}
          cy={cy}
          r={r + stroke / 2}
          fill="none"
          stroke="hsl(var(--hairline))"
          strokeWidth={1}
        />
        {segments.map((s) => (
          <circle
            key={s.name}
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke={s.color}
            strokeWidth={stroke}
            strokeDasharray={s.dash}
            strokeDashoffset={s.dashOffset}
            strokeLinecap="butt"
            className="transition-[stroke-dasharray] duration-500 ease-out"
          />
        ))}
      </svg>

      {/* Centre label (HTML overlay — better typographic control than SVG text) */}
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-mono text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Views
        </span>
        <span className="mt-1 font-display text-[40px] font-medium leading-none tracking-[-0.025em] numeral tabular-nums">
          {totalLabel}
        </span>
        {deltaPct !== undefined && deltaPct !== null && (
          <span
            className={
              deltaPct >= 0
                ? 'mt-2 inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 font-mono text-[10px] font-semibold tracking-[0.04em] text-success'
                : 'mt-2 inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 font-mono text-[10px] font-semibold tracking-[0.04em] text-destructive'
            }
          >
            <svg
              width="8"
              height="8"
              viewBox="0 0 10 10"
              aria-hidden
              className={deltaPct >= 0 ? '' : 'rotate-180'}
            >
              <path d="M5 2 L8 7 L2 7 Z" fill="currentColor" />
            </svg>
            {deltaPct > 0 ? '+' : ''}
            {deltaPct}%
          </span>
        )}
      </div>
    </div>
  );
}

interface RankRow {
  name: string;
  subtitle: string;
  clicks: number;
  inquiries: number;
  // Present on proptree rows only — agent headshot, initials fallback.
  image?: string | null;
  // Present on button rows only — drives the per-type glyph.
  button?: { url: string; type: string | null };
}

// Glyph for a profile button. The stored type only distinguishes
// link/video/gallery/whatsapp, so for plain links the URL decides the
// actual destination (tel:, Instagram, Maps, …). Falls back to a generic
// link icon.
type Glyph = {
  Icon: ComponentType<{ className?: string }>;
  brand?: 'whatsapp';
};

function buttonGlyph(url: string, type: string | null | undefined): Glyph {
  const u = url.toLowerCase();
  if (
    type === 'whatsapp' ||
    u.includes('wa.me/') ||
    u.includes('whatsapp.com')
  ) {
    return { Icon: WhatsAppIcon, brand: 'whatsapp' };
  }
  if (type === 'video') return { Icon: Play };
  if (type === 'gallery') return { Icon: Images };
  if (u.startsWith('tel:')) return { Icon: Phone };
  if (u.startsWith('mailto:')) return { Icon: Mail };
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch {
    return { Icon: Link2 };
  }
  const host = parsed.hostname.toLowerCase().replace(/^www\./, '');
  const is = (d: string) => host === d || host.endsWith(`.${d}`);
  if (is('instagram.com')) return { Icon: Instagram };
  if (is('facebook.com') || is('fb.com')) return { Icon: Facebook };
  if (is('linkedin.com')) return { Icon: Linkedin };
  if (is('youtube.com') || is('youtu.be')) return { Icon: Youtube };
  if (is('tiktok.com')) return { Icon: Music2 };
  if (is('x.com') || is('twitter.com')) return { Icon: Twitter };
  if (is('calendly.com') || is('cal.com')) return { Icon: Calendar };
  if (
    host === 'maps.google.com' ||
    (is('google.com') && parsed.pathname.startsWith('/maps')) ||
    is('goo.gl')
  ) {
    return { Icon: MapPin };
  }
  return { Icon: Link2 };
}

function ButtonGlyphChip({ row }: { row: RankRow }) {
  const { Icon, brand } = buttonGlyph(
    row.button?.url ?? '',
    row.button?.type ?? null,
  );
  return (
    <div
      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
        brand === 'whatsapp'
          ? 'bg-[#25D366]/10 text-[#25D366]'
          : 'bg-primary/10 text-primary'
      }`}
    >
      <Icon className="h-4 w-4" />
    </div>
  );
}

const TOP_BUTTONS: RankRow[] = [
  { name: 'View all listings', subtitle: 'proptr.ee/abi-cutler/listings', clicks: 2431, inquiries: 89 },
  { name: 'Contact on WhatsApp', subtitle: 'wa.me/971586931168', clicks: 1887, inquiries: 124 },
  { name: 'Book a viewing', subtitle: 'calendly.com/proptree-viewings', clicks: 1654, inquiries: 67 },
  { name: '4BR Villa · Dubai Marina', subtitle: '#NOVVI-6530', clicks: 1203, inquiries: 22 },
  { name: 'Penthouse · Palm Jumeirah', subtitle: '#NOVVI-4028', clicks: 987, inquiries: 18 },
  { name: 'Schedule a call', subtitle: 'calendly.com/proptree-call', clicks: 872, inquiries: 41 },
  { name: 'Properties for rent', subtitle: 'proptr.ee/rentals', clicks: 654, inquiries: 14 },
  { name: 'Instagram profile', subtitle: 'instagram.com/novvi', clicks: 521, inquiries: 0 },
  { name: '2BR · Downtown', subtitle: '#NOVVI-008508', clicks: 432, inquiries: 9 },
  { name: 'Email me', subtitle: 'mailto:abi@novviproperties.com', clicks: 398, inquiries: 12 },
];

const TOP_PROPTREES: RankRow[] = [
  { name: 'Adib Kabbara', subtitle: 'proptr.ee/adib', clicks: 4231, inquiries: 184 },
  { name: 'Abi Cutler', subtitle: 'proptr.ee/abi', clicks: 3872, inquiries: 156 },
  { name: 'Abdulrahman Hashmani', subtitle: 'proptr.ee/abdulrahman', clicks: 2543, inquiries: 94 },
  { name: 'Adrian Basdeo', subtitle: 'proptr.ee/adrian', clicks: 1987, inquiries: 62 },
  { name: 'Ahmed Ali', subtitle: 'proptr.ee/ahmed', clicks: 1654, inquiries: 48 },
  { name: 'Adam Sattar', subtitle: 'proptr.ee/adam', clicks: 1322, inquiries: 36 },
  { name: 'Aziza Bouziane', subtitle: 'proptr.ee/aziza', clicks: 984, inquiries: 29 },
  { name: 'Parveen Sohotey', subtitle: 'proptr.ee/parveen', clicks: 812, inquiries: 21 },
  { name: 'Sadiq Suberu', subtitle: 'proptr.ee/sadiq', clicks: 677, inquiries: 14 },
  { name: 'Driss Elmania', subtitle: 'proptr.ee/driss', clicks: 543, inquiries: 11 },
];

function topAgentsToRows(agents: TopAgent[] | undefined): RankRow[] {
  if (!agents) return [];
  return agents.map((a) => ({
    name: a.name,
    subtitle: a.alias ? `proptr.ee/${a.alias}` : a.id,
    clicks: a.clicks,
    inquiries: a.leads,
    image: a.image,
  }));
}

function topButtonsToRows(buttons: TopButton[] | undefined): RankRow[] {
  if (!buttons) return [];
  return buttons.map((b) => ({
    name: b.title,
    subtitle: b.url || b.id,
    clicks: b.clicks,
    inquiries: 0, // per-button leads aren't attributable from current data
    button: { url: b.url, type: b.type ?? null },
  }));
}

function RankingCard({
  eyebrow,
  title,
  rows,
  type,
  loading,
}: {
  eyebrow: string;
  title: string;
  rows: RankRow[];
  type: 'button' | 'proptree';
  loading?: boolean;
}) {
  return (
    <Card className="p-6">
      <div className="mb-5">
        <div className="eyebrow">{eyebrow}</div>
        <h2 className="mt-1 font-sans text-[18px] font-semibold tracking-[-0.01em]">
          {title}
        </h2>
      </div>
      {loading ? (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="h-8 w-8 shrink-0 animate-pulse rounded-full bg-muted" />
              <div className="h-3 flex-1 animate-pulse rounded bg-muted" />
              <div className="h-3 w-16 animate-pulse rounded bg-muted" />
            </div>
          ))}
        </div>
      ) : rows.length === 0 ? (
        <div className="py-8 text-center font-mono text-[11px] text-muted-foreground">
          No activity this month.
        </div>
      ) : (
      <div className="divide-y divide-[hsl(var(--hairline))]">
        {rows.map((row, i) => {
          const rank = i + 1;
          const convRate = row.clicks > 0 ? (row.inquiries / row.clicks) * 100 : 0;
          return (
            <div
              key={`${row.name}-${i}`}
              className="group flex items-center gap-3 py-2.5 first:pt-0 last:pb-0 md:gap-4"
            >
              <span className="w-6 shrink-0 font-mono text-[10px] font-bold tabular-nums text-muted-foreground">
                {String(rank).padStart(2, '0')}
              </span>
              {type === 'proptree' ? (
                row.image ? (
                  <img
                    src={row.image}
                    alt=""
                    className="h-8 w-8 shrink-0 rounded-full border border-[hsl(var(--hairline))] object-cover"
                  />
                ) : (
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted font-mono text-[10px] font-bold uppercase">
                    {initials(row.name)}
                  </div>
                )
              ) : (
                <ButtonGlyphChip row={row} />
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate font-sans text-[13px] font-medium">
                  {row.name}
                </div>
                <div className="truncate font-mono text-[10px] text-muted-foreground">
                  {row.subtitle}
                </div>
              </div>
              <div className="w-16 shrink-0 text-right">
                <div className="font-mono text-[13px] font-semibold tabular-nums">
                  {row.clicks.toLocaleString()}
                </div>
                <div className="mono-label text-muted-foreground">Clicks</div>
              </div>
              {type !== 'button' && (
                <>
                  <div className="w-12 shrink-0 text-right">
                    <div
                      className={`font-mono text-[13px] font-semibold tabular-nums ${
                        row.inquiries === 0 ? 'text-muted-foreground' : ''
                      }`}
                    >
                      {row.inquiries}
                    </div>
                    <div className="mono-label text-muted-foreground">Inq</div>
                  </div>
                  <div className="hidden w-14 shrink-0 text-right sm:block">
                    <div
                      className={`font-mono text-[11px] font-semibold tabular-nums ${
                        convRate >= 5
                          ? 'text-success'
                          : convRate >= 2
                            ? 'text-foreground'
                            : 'text-muted-foreground'
                      }`}
                    >
                      {convRate.toFixed(1)}%
                    </div>
                    <div className="mono-label text-muted-foreground">Conv</div>
                  </div>
                </>
              )}
            </div>
          );
        })}
      </div>
      )}
    </Card>
  );
}

function StatFromBucket({
  label,
  bucket,
  hint,
  loading,
}: {
  label: string;
  bucket: StatBucket | undefined;
  hint: string;
  loading?: boolean;
}) {
  if (loading || !bucket) {
    return (
      <div className="flex flex-col rounded-lg border border-[hsl(var(--hairline))] bg-card p-5">
        <div className="mono-label text-muted-foreground">{label}</div>
        <div className="mt-3 h-[40px] w-24 animate-pulse rounded bg-muted" />
        <div className="mt-2 h-3 w-32 animate-pulse rounded bg-muted" />
        <div className="mt-4 border-t border-[hsl(var(--hairline))] pt-3">
          <div className="h-3 w-full animate-pulse rounded bg-muted" />
        </div>
      </div>
    );
  }
  const delta =
    bucket.deltaPct === null
      ? '—'
      : `${bucket.deltaPct > 0 ? '+' : ''}${bucket.deltaPct}%`;
  return (
    <Stat
      label={label}
      value={bucket.thisMonth.toLocaleString()}
      delta={delta}
      hint={hint}
      total={bucket.sinceStart.toLocaleString()}
    />
  );
}

function Stat({
  label,
  value,
  delta,
  hint,
  total,
  tone = 'default',
}: {
  label: string;
  value: string;
  delta: string;
  hint: string;
  total?: string;
  tone?: 'default' | 'warn';
}) {
  const deltaCls =
    tone === 'warn'
      ? 'text-warning'
      : delta.startsWith('-')
        ? 'text-destructive'
        : 'text-success';
  return (
    <div className="flex flex-col rounded-lg border border-[hsl(var(--hairline))] bg-card p-5">
      <div className="flex items-center justify-between">
        <div className="mono-label text-muted-foreground">{label}</div>
        <div className={`font-mono text-[10px] font-semibold ${deltaCls}`}>{delta}</div>
      </div>
      <div className="mt-3 font-display text-[40px] font-medium leading-none tracking-[-0.02em] numeral">
        {value}
      </div>
      <div className="mt-2 font-mono text-[10px] text-muted-foreground">{hint}</div>
      {total && (
        <div className="mt-4 flex items-center justify-between border-t border-[hsl(var(--hairline))] pt-3">
          <span className="mono-label text-muted-foreground">Since start</span>
          <span className="font-mono text-[12px] font-semibold tabular-nums">{total}</span>
        </div>
      )}
    </div>
  );
}
